/**
 * Spec 028: US ZIP codes and Canadian postal codes.
 *
 * A Canadian postal code is "A1A 1A1": the first letter is never D, F, I, O,
 * Q, U, W or Z, and the other letters are never D, F, I, O, Q or U. It is
 * stored upper case with one space. Its first three characters (the FSA) are
 * the area a worker or a place to stay is shown at (spec D15): a full
 * Canadian code is often a single block.
 */
import { asCountry, CountryCode } from './regions';

const US_ZIP = /^\d{5}(-\d{4})?$/;
const CA_POSTAL = /^([ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z]) ?(\d[ABCEGHJ-NPRSTV-Z]\d)$/i;
const CA_FSA = /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z]$/i;

export const isValidPostalCode = (
  country: CountryCode | string | null | undefined,
  input: string | null | undefined,
): boolean => {
  const value = (input ?? '').trim();
  return asCountry(country) === 'CA' ? CA_POSTAL.test(value) : US_ZIP.test(value);
};

/** The stored form, or null when the code is not valid for the country. */
export const normalizePostalCode = (
  country: CountryCode | string | null | undefined,
  input: string | null | undefined,
): string | null => {
  const value = (input ?? '').trim();
  if (asCountry(country) === 'CA') {
    const match = CA_POSTAL.exec(value);
    return match ? `${match[1]} ${match[2]}`.toUpperCase() : null;
  }
  return US_ZIP.test(value) ? value : null;
};

/** The area a code is shown at: the 5-digit ZIP, or a Canadian code's FSA. */
export const postalArea = (
  country: CountryCode | string | null | undefined,
  input: string | null | undefined,
): string | null => {
  const normalized = normalizePostalCode(country, input);
  if (!normalized) return null;
  return asCountry(country) === 'CA' ? normalized.slice(0, 3) : normalized.slice(0, 5);
};

/**
 * An area code a worker lists as "also served": a 5-digit ZIP, or on a
 * Canadian brand a FSA ("M5V") or a full postal code (kept as its FSA).
 */
export const normalizeServiceArea = (
  country: CountryCode | string | null | undefined,
  input: string | null | undefined,
): string | null => {
  const value = (input ?? '').trim();
  if (asCountry(country) === 'CA') {
    if (CA_FSA.test(value)) return value.toUpperCase();
    return postalArea('CA', value);
  }
  return /^\d{5}$/.test(value) ? value : null;
};

export const postalCodeErrorMessage = (country: CountryCode | string | null | undefined): string =>
  asCountry(country) === 'CA' ? 'Enter a Canadian postal code (A1A 1A1).' : 'Enter a US ZIP code.';

export const postalCodeLabel = (country: CountryCode | string | null | undefined): string =>
  asCountry(country) === 'CA' ? 'Postal code' : 'Zip code';

export const regionLabel = (country: CountryCode | string | null | undefined): string =>
  asCountry(country) === 'CA' ? 'Province' : 'State';
